import React, { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { jobsApi, customersApi, propertiesApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = x.getDate() - day;
  x.setDate(diff); x.setHours(0, 0, 0, 0);
  return x;
}

export default function CalendarPage() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [mode, setMode] = useState('week'); // week | month

  useEffect(() => {
    (async () => {
      try {
        const [js, cs, ps] = await Promise.all([jobsApi.list(), customersApi.list(), propertiesApi.list()]);
        setJobs(js);
        setCustomers(cs);
        setProperties(ps);
      } catch {
        // surfaced by the api response interceptor
      }
    })();
  }, []);

  const days = useMemo(() => {
    if (mode === 'week') {
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        return d;
      });
    }
    // month
    const firstOfMonth = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
    const start = startOfWeek(firstOfMonth);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [weekStart, mode]);

  const jobsByDay = useMemo(() => {
    const map = {};
    (jobs || []).forEach(j => {
      if (!j.scheduled_at) return;
      const k = new Date(j.scheduled_at).toDateString();
      (map[k] = map[k] || []).push(j);
    });
    return map;
  }, [jobs]);

  const shift = (delta) => {
    const d = new Date(weekStart);
    if (mode === 'week') d.setDate(d.getDate() + 7 * delta);
    else d.setMonth(d.getMonth() + delta);
    setWeekStart(startOfWeek(d));
  };

  return (
    <AppShell title="Calendar">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Calendar</h1>
          <p className="text-sm text-muted-foreground">Weekly and monthly schedule view.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="border rounded-lg p-1 flex gap-1 bg-card">
            <button className={`px-3 py-1.5 text-sm rounded-md transition-colors ${mode === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} onClick={() => setMode('week')}>Week</button>
            <button className={`px-3 py-1.5 text-sm rounded-md transition-colors ${mode === 'month' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`} onClick={() => setMode('month')}>Month</button>
          </div>
          <Button variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Seven columns cannot fit a phone: 375px / 7 is ~37px per day, narrower
          than a two-digit date. The grid keeps a usable width and scrolls. */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <div className="grid grid-cols-7 gap-2 min-w-[680px] sm:min-w-0">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="text-xs uppercase tracking-wide text-muted-foreground text-center font-medium py-1">{d}</div>
        ))}
        {days.map(d => {
          const key = d.toDateString();
          const list = jobsByDay[key] || [];
          const isToday = new Date().toDateString() === key;
          return (
            <Card key={key} className={`min-h-[120px] ${isToday ? 'border-primary' : ''}`}>
              <CardContent className="p-2">
                <div className={`text-xs mb-1 ${isToday ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {list.slice(0, 4).map(j => {
                    const c = customers.find(x => x.id === j.customer_id);
                    const p = properties.find(x => x.id === j.property_id);
                    return (
                      <Link key={j.id} to={`/jobs/${j.id}`} className={`block rounded-md border px-1.5 py-1 text-[11px] status-${j.status}`}>
                        <div className="font-medium truncate">{p?.nickname || c?.name || 'Job'}</div>
                        <div className="opacity-75">{new Date(j.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </Link>
                    );
                  })}
                  {list.length > 4 && <div className="text-[10px] text-muted-foreground">+{list.length - 4} more</div>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      </div>
    </AppShell>
  );
}
